import {VerticalType} from '../products/products.enums';
import {DateTime} from 'luxon';
import {AgencyLinkStatus, PermissionScope} from '../agency/agency.types';
import {MetricValue} from '../analytics/analytics.types';

interface BaseClient {
  id: number;
  name: string;
  encId: string;
  isActive: boolean;
  clientType: ClientType;
  vertical: VerticalType;
}

interface AssociatedAgency {
  accountId: number;
  accountName: string;
  squareLogoUrl: string;
}

interface RemoteAdvertiser extends BaseClient {
  initiatedBy: ClientType;
  linkStatus: AgencyLinkStatus;
  permissionScope: PermissionScope;
  isOwnerAgency: boolean;
  logoUrl: string;
  createdDate: string;
  createdByUser: {
    firstName: string;
    lastName: string;
    profilePictureUrl: string;
    id: number;
  };
  associatedAgencies?: AssociatedAgency[];
  squareLogoUrl?: string;
}

interface Advertiser extends Omit<RemoteAdvertiser, 'createdDate'> {
  createdDate: DateTime;
  analytics: Record<MetricValue, number>;
}

enum ClientWhitelistedCapability {
  None = 'NONE',
  UserProfiling = 'UserProfiling',
  WeatherTargeting = 'WeatherTargeting',
  ProductsUpdateByApi = 'ProductsUpdateByApi',
  PriceEnrichmentByDatesOrStore = 'PriceEnrichmentByDatesOrStore',
  UseMaxMindForDisplayIp2Location = 'UseMaxMindForDisplayIp2Location',
  SportServices = 'SportServices',
  ConversionAttributionUsingClinchClickId = 'ConversionAttributionUsingClinchClickId',
  EnableMediaCost = 'EnableMediaCost',
}

enum ClientIdentityProviderName {
  NeustarAd = 'neustarAd',
  IntentIQAd = 'intentIQAd',
  IntentIQWeb = 'intentIQWeb',
  LiveRampInWeb = 'liveRampInWeb',
  LiveRampInAds = 'liveRampInAds',
  NeustarWeb = 'neustarWeb',
  LiveRampEnrichmentByDevice = 'liveRampEnrichmentByDevice',
}

interface CampaignWorkflowSettings {
  adsApprovers: number[];
  briefApprovers: number[];
  campaignLogicApprovers: number[];
  conceptPlanApprovers: number[];
  isBriefEnabled: boolean;
  isConceptPlanEnable: boolean;
  isTearsheetEnabled: boolean;
  placementsApprovers: number[];
  trackersApprovers: number[];
  tearsheetApprovers: number[];
  homeApprovers: number[];
}

interface AccountProfile {
  name: string;
  defaultCurrencyCode: string;
  defaultTimezone: string;
  defaultDateFormat: string;
  is24h: boolean;
  additionalProductTypes: number[];
  companyInfo: CompanyInfo;

  securitySettings: SecuritySettings;
  servicesSettings: ServicesSettings;
  analyticsSettings: AnalyticsSettings;
  creativeTagNames: string[];
  campaignTagNames: string[];
  clientType: ClientType;

  campaignWorkflowSettings: CampaignWorkflowSettings;
}

interface Client extends AccountProfile {
  id: number;
  adminSettings: AdminSettings;
  vertical: number;
  isDemo: boolean;
  isActive: boolean;
  encId: string;
  viewOnlyToken: string;
  defaultProductTypes: number[];
  allSupportedProducts: number[];
  groupedAccounts: number[];
}

type RemoteClient = Omit<Client, 'id' | 'allSupportedProducts'> & {
  clientId: number;
};

enum AdminSettingsField {
  UserProfiling = 'isUserProfilingEnabled',
  NeustarWeb = 'isNeustarWebEnabled',
  NeustarAds = 'isNeustarAdsEnabled',
  IntentIQWeb = 'isIntentIQWebEnabled',
  IntentIQAds = 'isIntentIQAdsEnabled',
  LiveRampWeb = 'isLiveRampWebEnabled',
  LiveRampAds = 'isLiveRampAdsEnabled',
  LiveRampSyncPixelInAds = 'isLiveRampSyncPixelInAdsEnabled',
  LiveRampSyncPixelInWeb = 'isLiveRampSyncPixelInWebEnabled',
  LiveRampEnrichmentByDevice = 'isLiveRampEnrichmentByDeviceEnabled',
  CreativeExport = 'isCreativeExportEnabled',
  TreasureDataCookieSync = 'isTreasureDataCookieSyncEnabled',
  MaxRowsInReport = 'maxRowsInReport',
  MaxVideoVariationsInCampaigns = 'maxVideoVariationsInCampaigns',
  MaxCampaigns = 'maxCampaigns',
  MaxProductFeeds = 'maxProductFeeds',
  MaxTotalProductsInAllFeeds = 'maxTotalProductsInAllFeeds',
  MaxProductsInSingleFeeds = 'maxProductsInSingleFeeds',
  MaxProductSetsInFeed = 'maxProductSetsInFeed',
  MaxProductsInTopItems = 'maxProductsInTopItems',
  MaxLocationFeeds = 'maxLocationFeeds',
  MaxTotalLocationsInAllFeeds = 'maxTotalLocationsInAllFeeds',
  MaxLocationInSingleFeeds = 'maxLocationInSingleFeeds',
  MaxCreatives = 'maxCreatives',
  MaxPixels = 'maxPixels',
  MaxProductsInProductSet = 'maxProductsInProductSet',
  MaxTotalProductsSets = 'maxTotalProductsSets',
  MaxTotalTopItems = 'maxTotalTopItems',
  MaxTotalCategoryTopItems = 'maxTotalCategoryTopItems',
  MaxTotalVideoVariations = 'maxTotalVideoVariations',
  MaxPlacementsPerCampaignVersion = 'maxPlacementsPerCampaignVersion',
  MaxTrackersPerCampaignVersion = 'maxTrackersPerCampaignVersion',
  MaxContentFeeds = 'maxContentFeeds',
  MaxTotalContentItemsInAllFeeds = 'maxTotalContentItemsInAllFeeds',
  MaxContentItemsInSingleFeeds = 'maxContentItemsInSingleFeeds',
  IsOutputDataFeedsEnabled = 'isOutputDataFeedsEnabled',
  IsRequiresPingForAttributionParameterEnabled = 'isRequiresPingForAttributionParameterEnabled',
  IsOutputDataFeedsStandardLogsEnabled = 'isOutputDataFeedsStandardLogsEnabled',
  IsPublishTagsAsPausedEnabled = 'isPublishTagsAsPausedEnabled',
  IsOutputDataFeedsConnectedLogsEnabled = 'isOutputDataFeedsConnectedLogsEnabled',
  IsOutputDataFeedsCVREnabled = 'isOutputDataFeedsCVREnabled',
  MaxSTDLogsDataOutputs = 'maxSTDLogsDataOutputs',
  MaxConnectedLogsDataOutputs = 'maxConnectedLogsDataOutputs',
  MaxServingLogsForMediaPartners = 'maxServingLogsForMediaPartners',
  BrandName = 'brandName',
  MsaType = 'msaType',
  HoldingGroup = 'holdingGroup',
  MediaAgency = 'mediaAgency',
  CustomerRegion = 'customerRegion',
  CountryOfOperation = 'countryOfOperation',
  State = 'state',
  Vertical = 'vertical',
  ClinchTeam = 'clinchTeam',
  ClinchAccountLead = 'clinchAccountLead',
  ClinchTechLead = 'clinchTechLead',
  ClinchSalesLead = 'clinchSalesLead',
  CreativeScore = 'isCreativeScoreEnabled',
}

enum OutputDataFeedsSettingsField {
  IsOutputDataFeedsStandardLogsEnabled = 'isOutputDataFeedsStandardLogsEnabled',
  IsOutputDataFeedsConnectedLogsEnabled = 'isOutputDataFeedsConnectedLogsEnabled',
  IsOutputDataFeedsCVREnabled = 'isOutputDataFeedsCVREnabled',
}

interface OutputDataFeedsSettings {
  [OutputDataFeedsSettingsField.IsOutputDataFeedsStandardLogsEnabled]: boolean;
  [OutputDataFeedsSettingsField.IsOutputDataFeedsConnectedLogsEnabled]: boolean;
  [OutputDataFeedsSettingsField.IsOutputDataFeedsCVREnabled]: boolean;
}

interface AdminSettings extends OutputDataFeedsSettings {
  [AdminSettingsField.UserProfiling]: boolean;
  [AdminSettingsField.NeustarWeb]: boolean;
  [AdminSettingsField.NeustarAds]: boolean;
  [AdminSettingsField.IntentIQWeb]: boolean;
  [AdminSettingsField.IntentIQAds]: boolean;
  [AdminSettingsField.LiveRampWeb]: boolean;
  [AdminSettingsField.LiveRampAds]: boolean;
  [AdminSettingsField.LiveRampSyncPixelInAds]: boolean;
  [AdminSettingsField.LiveRampSyncPixelInWeb]: boolean;
  [AdminSettingsField.LiveRampEnrichmentByDevice]: boolean;
  [AdminSettingsField.CreativeExport]: boolean;
  [AdminSettingsField.TreasureDataCookieSync]: boolean;
  [AdminSettingsField.MaxRowsInReport]: number;
  [AdminSettingsField.MaxVideoVariationsInCampaigns]: number;
  [AdminSettingsField.MaxCampaigns]: number;
  [AdminSettingsField.MaxProductFeeds]: number;
  [AdminSettingsField.MaxTotalProductsInAllFeeds]: number;
  [AdminSettingsField.MaxProductsInSingleFeeds]: number;
  [AdminSettingsField.MaxProductSetsInFeed]: number;
  [AdminSettingsField.MaxProductsInTopItems]: number;
  [AdminSettingsField.MaxLocationFeeds]: number;
  [AdminSettingsField.MaxTotalLocationsInAllFeeds]: number;
  [AdminSettingsField.MaxLocationInSingleFeeds]: number;
  [AdminSettingsField.MaxCreatives]: number;
  [AdminSettingsField.MaxPixels]: number;
  [AdminSettingsField.MaxProductsInProductSet]: number;
  [AdminSettingsField.MaxTotalProductsSets]: number;
  [AdminSettingsField.MaxTotalTopItems]: number;
  [AdminSettingsField.MaxTotalCategoryTopItems]: number;
  [AdminSettingsField.MaxTotalVideoVariations]: number;
  [AdminSettingsField.MaxPlacementsPerCampaignVersion]: number;
  [AdminSettingsField.MaxTrackersPerCampaignVersion]: number;
  [AdminSettingsField.MaxContentFeeds]: number;
  [AdminSettingsField.MaxTotalContentItemsInAllFeeds]: number;
  [AdminSettingsField.MaxContentItemsInSingleFeeds]: number;
  [AdminSettingsField.MaxSTDLogsDataOutputs]: number;
  [AdminSettingsField.MaxConnectedLogsDataOutputs]: number;
  [AdminSettingsField.MaxServingLogsForMediaPartners]: number;

  [AdminSettingsField.IsOutputDataFeedsEnabled]: boolean;
  [AdminSettingsField.IsRequiresPingForAttributionParameterEnabled]: boolean;
  [AdminSettingsField.IsPublishTagsAsPausedEnabled]: boolean;

  [AdminSettingsField.BrandName]: string;
  [AdminSettingsField.MsaType]: string;
  [AdminSettingsField.HoldingGroup]: string;
  [AdminSettingsField.MediaAgency]: string;
  [AdminSettingsField.CustomerRegion]: string;
  [AdminSettingsField.CountryOfOperation]: string;
  [AdminSettingsField.State]: string;
  [AdminSettingsField.Vertical]: string;
  [AdminSettingsField.ClinchTeam]: string;
  [AdminSettingsField.ClinchAccountLead]: number[];
  [AdminSettingsField.ClinchTechLead]: number[];
  [AdminSettingsField.ClinchSalesLead]: number[];
  [AdminSettingsField.CreativeScore]: boolean;
}

interface CompanyInfo {
  id: string;
  vatId: string;
  mainPhone: string;
  accountingPhone: string;
  streetAddress: string;
  city: string;
  countryCode: string;
  zip: string;
  accountManagerEmails: string[];
  clientManagerEmails: string[];
  invoicingEmail: string[];
  logoUrl: string;
  squareLogoUrl: string;
}

interface SecuritySettings {
  isMultiFactorEnabled: boolean;
  isPublicLinkEnabled: boolean;
  isSuspendUsersEnabled: boolean;
  daysForInactivityBeforeSuspension: number;
}

interface ServicesSettings {
  isIp2LocationEnabled: boolean;
  isWeatherTargetingEnabled: boolean;
  isSportServicesEnabled: boolean;
  isMediaCostEnabled: boolean;
  isConversionAttributionUsingClinchClickIdEnabled: boolean;
  isBackgroundRemovalInFeedsEnabled: boolean;
}

interface AnalyticsSettings {
  isVtcInTotalSalesEnabled: boolean;
  isSpentInAnalyticsPageEnabled: boolean;
  isTotalSalesInAnalyticsPageEnabled: boolean;
}

interface ConsumedQuotas {
  campaigns: number;
  productFeeds: number;
  totalProductsInAllFeeds: number;
  locationFeeds: number;
  totalLocationsInAllFeeds: number;
  creativeStyles: number;
  pixels: number;
  totalProductsSets: number;
  totalTopItems: number;
  totalCategoryTopItems: number;
  totalVideoVariations: number;
  contentFeeds: number;
  totalContentItemsInAllFeeds: number;
  stdLogsDataOutputs: number;
  connectedLogsDataOutputs: number;
  servingLogsForMediaPartners: number;
}

enum ClientType {
  Advertiser = 10,
  Agency = 20,
  Clinch = 30,
}

type AddClientProps = {
  name: string;
  type?: ClientType;
  vertical?: number;
  agencyId?: number;
};

interface AgencySettings extends OutputDataFeedsSettings {
  clientId: number;
  defaultDatetimeFormat: string;
  isMultiFactorEnabled: boolean;
}

export {
  BaseClient,
  ClientIdentityProviderName,
  ClientWhitelistedCapability,
  AccountProfile,
  Client,
  AdminSettings,
  CompanyInfo,
  SecuritySettings,
  ServicesSettings,
  AnalyticsSettings,
  ConsumedQuotas,
  RemoteClient,
  ClientType,
  AddClientProps,
  Advertiser,
  RemoteAdvertiser,
  CampaignWorkflowSettings,
  VerticalType,
  AgencySettings,
  AdminSettingsField,
  OutputDataFeedsSettings,
};
